/* eslint-disable local/ensure-wac-token-usage */
import { useEffect } from "react";

import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import Button from "@atlaskit/button";
import { media } from "@atlaskit/primitives";

import { Logo } from "@/libraries/wpl/foundations/src";

import { useAnalyticsClient } from "../../../../../../analytics/wac-analytics/analytics-client";
import { useRecentJiraSite } from "../../utils/use-recent-jira-site";

import {
    TRACKING_EVENT_NAMES,
    loggedInHeroAnalyticsPayload,
    loggedInHeroLoadedAnalyticsPayload
} from "./analytics";
import ArrowCircle from "./assets/arrow-circle";
import Target from "./assets/target";

// @ts-ignore
const GRAPHQL_GATEWAY_URL =
    process.env["NODE_ENV"] === "production"
        ? "https://www.atlassian.com/gateway/api/graphql"
        : "https://wac.stg.internal.atlassian.com/gateway/api/graphql";

const START_URL =
    process.env["NODE_ENV"] === "production"
        ? "https://start.atlassian.com"
        : "https://start.stg.atlassian.com";

const jswHeroSectionStyle = css({
    backgroundImage: "linear-gradient(180deg, #253858 0%, #091E42 100%)",
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 15px"
});

const targetStyle = css({
    position: "absolute",
    insetInlineStart: "0",
    insetBlockStart: "0",
    [media.below.md]: {
        display: "none"
    }
});

const arrowCircleStyle = css({
    position: "absolute",
    insetInlineEnd: "0",
    insetBlockStart: "250px",
    [media.below.md]: {
        display: "none"
    }
});

const heroTextSectionStyle = css({
    color: "white",
    width: "100%",
    maxWidth: "928px",
    margin: "100px 170px 50px 170px",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    p: {
        fontSize: "20px",
        lineHeight: "28px",
        fontWeight: "400"
    },
    [media.above.md]: {
        marginBlockStart: "100px",
        marginBlockEnd: "50px",
        marginInline: "170px"
    },
    [media.below.md]: {
        marginBlockStart: "25pt",
        marginInline: "15px"
    }
});

const jiraSiteBoxStyle = css({
    width: "100%",
    height: "72px",
    display: "flex",
    justifyContent: "space-between",
    flexDirection: "row",
    alignItems: "center",

    border: "2px solid #579DFF",
    padding: "10px 10px",
    marginBlockStart: "20px",
    marginBlockEnd: "20px"
});

const headingStyle = css({
    fontSize: "48px",
    lineHeight: "56px",
    fontWeight: "500",
    marginBlockEnd: "15px"
});

const siteStyle = css({
    lineHeight: "22px",
    fontSize: "16px",
    overflow: "hidden"
});

const productStyle = css({
    fontWeight: "500",
    fontSize: "17px"
});

const siteNameStyle = css({
    fontWeight: "300",
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap"
});

const buttonStyle = css({
    marginInlineStart: "15px"
});

const otherJiraSiteStyle = css({
    marginBlockStart: "15px",
    fontSize: "16px",
    textDecoration: "underline",
    color: "white",
    fontWeight: "400px"
});

const subHeadingStyle = css({
    marginBlockStart: "15px",
    marginBlockEnd: "15px"
});

const logoStyle = css({
    marginInlineEnd: "10px",
    marginBlockStart: "9px"
});

const logoAndSiteNameStyle = css({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    minWidth: "0px"
});

const LoggedInHeroSection = () => {
    const { splitName, hasMultipleJiras, siteName, siteUrl } =
        useRecentJiraSite();

    const { createAnalyticsEvent } = useAnalyticsEvents();
    const analyticsClient = useAnalyticsClient();

    useEffect(() => {
        //if client side, fire analytics event
        if (typeof window !== "undefined") {
            createAnalyticsEvent({
                ...loggedInHeroLoadedAnalyticsPayload
            }).fire();
        }
    }, [analyticsClient, createAnalyticsEvent]);

    const clickHandler = (eventName: TRACKING_EVENT_NAMES) => {
        createAnalyticsEvent({
            ...loggedInHeroAnalyticsPayload,
            attributes: {
                eventName,
                url: window.location.href,
                atlasId: "ATLAS-53087"
            }
        }).fire();
    };

    return (
        <>
            <section css={jswHeroSectionStyle}>
                <div css={targetStyle}>
                    <Target />
                </div>
                <div css={arrowCircleStyle}>
                    <ArrowCircle />
                </div>
                <div css={heroTextSectionStyle}>
                    <h1 css={headingStyle}>
                        {splitName && splitName.length > 0 ? (
                            <FormattedMessage
                                id="JiraSoftwarePageTemplate.loggedInHero.welcomeBackWithName"
                                defaultMessage={`Welcome back, {name}.`}
                                values={{ name: splitName[0] }}
                            />
                        ) : (
                            <FormattedMessage
                                id="JiraSoftwarePageTemplate.loggedInHero.welcomeBack"
                                defaultMessage="Welcome back"
                            />
                        )}
                    </h1>
                    <p css={subHeadingStyle}>
                        <FormattedMessage
                            id="JiraSoftwarePageTemplate.loggedInHero.pickUpWhereYouLeftOff"
                            defaultMessage="Pick up where you left off in Jira."
                        />
                    </p>
                    <a
                        css={jiraSiteBoxStyle}
                        href={siteUrl}
                        onClick={() =>
                            clickHandler(
                                TRACKING_EVENT_NAMES.loggedInHeroProductCtaClicked
                            )
                        }
                    >
                        <div css={logoAndSiteNameStyle}>
                            <div css={logoStyle}>
                                <Logo
                                    brand="jira"
                                    type="mark"
                                    appearance="brand"
                                />
                            </div>
                            <div css={siteStyle}>
                                <div css={productStyle}>Jira</div>
                                <div css={siteNameStyle}>{siteName}</div>
                            </div>
                        </div>
                        <Button
                            id="jira-button"
                            appearance="warning"
                            css={buttonStyle}
                        >
                            <FormattedMessage
                                id="JiraSoftwarePageTemplate.loggedInHero.goToJira"
                                defaultMessage="Go to Jira"
                            />
                        </Button>
                    </a>
                    {hasMultipleJiras && (
                        <Button
                            href={START_URL}
                            appearance="subtle-link"
                            onClick={() =>
                                clickHandler(
                                    TRACKING_EVENT_NAMES.loggedInHeroAnotherSiteCtaClicked
                                )
                            }
                        >
                            <span css={otherJiraSiteStyle}>
                                <FormattedMessage
                                    id="JiraSoftwarePageTemplate.loggedInHero.lookingForAnotherSite"
                                    defaultMessage="I’m looking for another Jira site"
                                />
                            </span>
                        </Button>
                    )}
                </div>
            </section>
        </>
    );
};

LoggedInHeroSection.displayName = "LoggedInHeroSection";

export default LoggedInHeroSection;
